import { Artefact, ArtefactType } from "@/types/artefacts";

export type DownloadFormat =
  | "png"
  | "pdf-visual"
  | "pdf-text"
  | "excel"
  | "csv"
  | "word"
  | "word-visual"
  | "powerpoint"
  | "powerpoint-visual"
  | "html-source"
  | "html-interactive"
  | "markdown"
  | "text";

export interface FormatDetectionResult {
  hasTable: boolean;
  hasChart: boolean;
  isTextHeavy: boolean;
  contentType: "chart" | "table" | "text" | "mixed";
}

/**
 * Detects available download formats based on artefact content
 */
export const detectAvailableFormats = (artefact: Artefact): DownloadFormat[] => {
  switch (artefact.type) {
    case ArtefactType.HTML:
      return detectHTMLFormats(artefact);
    case ArtefactType.TABLE:
      return ["excel", "powerpoint", "csv"];
    case ArtefactType.DOCUMENT:
      return ["word", "powerpoint", "pdf-text", "markdown"];
    case ArtefactType.CODE:
      return ["pdf-visual", "text", "pdf-text"];
    default:
      return ["text"];
  }
};

/**
 * Analyzes HTML content to determine available export formats
 */
const detectHTMLFormats = (artefact: Artefact): DownloadFormat[] => {
  const analysis = analyzeHTMLContent(artefact.content);

  const formats: DownloadFormat[] = [];

  // Office formats first (Word, PowerPoint, Excel)
  formats.push("word-visual", "powerpoint-visual");

  // Excel export if tables detected
  if (analysis.hasTable) {
    formats.push("excel");
  }

  // PDF formats next
  if (analysis.hasChart || !analysis.isTextHeavy) {
    formats.push("pdf-visual");
  }

  // PNG image format
  formats.push("png");

  // Interactive/source formats
  formats.push("html-interactive", "html-source");

  // Text formats last
  if (analysis.isTextHeavy) {
    formats.push("pdf-text", "word");
  }

  return formats;
};

/**
 * Analyzes HTML content structure
 */
export const analyzeHTMLContent = (htmlContent: string): FormatDetectionResult => {
  // Create a temporary DOM parser
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");

  // Detect tables
  const tables = doc.querySelectorAll("table");
  const hasTable = tables.length > 0;

  // Detect chart libraries and canvas elements
  const hasChartJS = htmlContent.includes("chart.js") || htmlContent.includes("Chart.js");
  const hasPlotly = htmlContent.includes("plotly") || htmlContent.includes("Plotly");
  const hasD3 = htmlContent.includes("d3.js") || htmlContent.includes("d3.min.js");
  const hasCanvas = doc.querySelectorAll("canvas").length > 0;
  const hasSVG = doc.querySelectorAll("svg").length > 0;

  const hasChart =
    hasChartJS || hasPlotly || hasD3 || hasCanvas || hasSVG || htmlContent.includes("chart");

  // Analyze text content
  const textContent = doc.body.textContent || "";
  const wordCount = textContent.trim().split(/\s+/).length;
  const elementCount = doc.body.querySelectorAll("*").length;

  // Consider content text-heavy if word count is high relative to element count
  const isTextHeavy = wordCount > 100 && wordCount / elementCount > 5;

  // Determine primary content type
  let contentType: "chart" | "table" | "text" | "mixed" = "text";

  if (hasChart && hasTable) {
    contentType = "mixed";
  } else if (hasChart) {
    contentType = "chart";
  } else if (hasTable) {
    contentType = "table";
  }

  return {
    hasTable,
    hasChart,
    isTextHeavy,
    contentType,
  };
};

/**
 * Checks if HTML content can be exported to a specific format
 */
export const canExportToFormat = (artefact: Artefact, format: DownloadFormat): boolean => {
  const availableFormats = detectAvailableFormats(artefact);

  return availableFormats.includes(format);
};

/**
 * Extracts table data from HTML content
 */
export const extractTablesFromHTML = (
  htmlContent: string,
): Array<{ headers: string[]; rows: string[][] }> => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, "text/html");
  const tables = doc.querySelectorAll("table");

  return Array.from(tables).map((table) => {
    // Extract headers
    const headerCells = table.querySelectorAll("thead th, thead td");
    const headers = Array.from(headerCells).map((cell) => cell.textContent?.trim() || "");

    // If no thead, try first row
    if (headers.length === 0) {
      const firstRow = table.querySelector("tr");

      if (firstRow) {
        const firstRowCells = firstRow.querySelectorAll("th, td");

        headers.push(...Array.from(firstRowCells).map((cell) => cell.textContent?.trim() || ""));
      }
    }

    // Extract rows
    const bodyRows = table.querySelectorAll("tbody tr, tr");
    const rows = Array.from(bodyRows)
      .slice(headers.length > 0 ? 1 : 0) // Skip first row if it was used as headers
      .map((row) => {
        const cells = row.querySelectorAll("td, th");

        return Array.from(cells).map((cell) => cell.textContent?.trim() || "");
      });

    return { headers, rows };
  });
};
